import { useEffect, useMemo, useState } from 'react'
import { useTimeline } from '../context/useTimeline'
import { useWorkspace } from '../context/useWorkspace'
import { useCanvasPermissions } from './useCanvasPermissions'
import { firestoreDb } from '../services/firebase'
import {
  subscribeWorkspaceActivityFirstPage,
  type AuditActivityFilters,
  type WorkspaceActivityRecord,
} from '../services/activityService'
import type { DashboardView } from '../components/Common/MobileDashboardDock'
import { buildViewProjectionSnapshot } from '../utils/viewProjection'
import { activityBucketsByDate, buildActivityHeatmapBuckets } from '../utils/activityHeatmapProjection'
import { useChecklistLockState, type LockTarget } from './useChecklistLockState'
import { useWorkspaceDay } from './useWorkspaceDay'

const activityViews = new Set<DashboardView>(['Heatmap', 'WarRoom'])
const EMPTY_ACTIVITY_RECORDS: WorkspaceActivityRecord[] = []

export const useViewProjection = (viewType: DashboardView) => {
  const timeline = useTimeline()
  const { activeWorkspaceId, workspaces } = useWorkspace()
  const { canReadElement } = useCanvasPermissions()
  const isEnabled = Boolean(activityViews.has(viewType) && activeWorkspaceId)
  const key = isEnabled ? `${activeWorkspaceId}:${viewType}` : ''

  const [subscribedActivity, setSubscribedActivity] = useState<{
    key: string
    records: WorkspaceActivityRecord[]
  } | null>(null)

  const { timeZone, todayIso, weekKey } = useWorkspaceDay(activeWorkspaceId)
  const activeWorkspaceTimezone = timeZone ?? workspaces.find((workspace) => workspace.id === activeWorkspaceId)?.timezone

  useEffect(() => {
    if (!activityViews.has(viewType) || !activeWorkspaceId) {
      return
    }

    const currentKey = `${activeWorkspaceId}:${viewType}`
    const to = new Date()
    const from = new Date(to)
    from.setDate(from.getDate() - 29)
    from.setHours(0, 0, 0, 0)
    const filters: AuditActivityFilters = {
      from,
      to,
      memberIds: [],
      itemIds: [],
      activityTypes: [],
    }
    return subscribeWorkspaceActivityFirstPage(
      firestoreDb,
      activeWorkspaceId,
      filters,
      (result) => {
        setSubscribedActivity({ key: currentKey, records: result.records })
      },
      () => {
        setSubscribedActivity({ key: currentKey, records: EMPTY_ACTIVITY_RECORDS })
      },
      500,
    )
  }, [activeWorkspaceId, viewType])

  const isSubscribed = Boolean(isEnabled && subscribedActivity && subscribedActivity.key === key)
  const activityRecords = isSubscribed && subscribedActivity ? subscribedActivity.records : EMPTY_ACTIVITY_RECORDS
  const isActivityLoading = Boolean(isEnabled && !isSubscribed)

  const lockTargets = useMemo<LockTarget[]>(() => {
    const items = timeline.checklistItems ?? []
    return items.map((item) => ({
      itemId: item.id,
      periodKey: item.checklistType === 'WEEKLY' ? weekKey : todayIso,
    }))
  }, [timeline.checklistItems, todayIso, weekKey])

  const lockState = useChecklistLockState(timeline.checklistItems ?? [], lockTargets, {
    workspaceId: activeWorkspaceId,
    timeZone: activeWorkspaceTimezone,
  })

  const projectionSnapshot = useMemo(
    () => buildViewProjectionSnapshot(timeline, viewType, canReadElement, {
      activityRecords,
      timezone: activeWorkspaceTimezone,
      pages: lockState.pages,
    }),
    [activeWorkspaceTimezone, activityRecords, canReadElement, timeline, viewType, lockState.pages],
  )

  const activityByDate = useMemo(
    () => activityBucketsByDate(buildActivityHeatmapBuckets(activityRecords, {
      days: 30,
      timeZone: activeWorkspaceTimezone,
    })),
    [activityRecords, activeWorkspaceTimezone],
  )

  return {
    viewType,
    visualItems: projectionSnapshot.visualItems,
    activityRecords,
    activityByDate,
    isActivityLoading,
    riskByElementId: projectionSnapshot.riskByElementId,
    dependencyEdges: projectionSnapshot.dependencyEdges,
    urgencyBuckets: projectionSnapshot.urgencyBuckets,
    lifecycleGroups: projectionSnapshot.lifecycleGroups,
    todayIso: projectionSnapshot.todayIso,
    workspaceTimezone: activeWorkspaceTimezone,
  }
}
